import { useState, useEffect } from "react";
import { Box } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface Activity {
  id: number;
  title: string;
  icon: string;
  sort_order: number;
  status: boolean;
}

export default function CurricularActivities() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getActivities();
  }, [page, limit, search]);

  const getActivities = async () => {
    setLoading(true);
    const response = await request.post("curricular-activities", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setActivities(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 180,
      format: (value) => value,
    },
    {
      id: "icon",
      label: "Icon",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/curricular-activities/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.title }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Curricular Activities"
        showSearch
        searchPlaceholder="Search activities..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Activity",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/curricular-activities/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={activities}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Activity - " + deleteDialog?.title}
          message="Are you sure you want to delete this activity? This action cannot be undone."
          apiEndpoint={"curricular-activities/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getActivities();
          }}
        />
      </PageWrapper>
    </>
  );
}